import "../Matches/PortalSurfaces.css";
import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useLocation, useParams } from "react-router-dom";
import { getMatch, getMatchScoreDetail, getMatchSetupDetail, getStageScoreDetail } from "../../lib/leaderboardApi";
import { ScoreDetailRenderer } from "./JsonRenderers";
import ScoreActionLog from "./ScoreActionLog";
import "./Leaderboard.css";
import { resultsReturn } from "../Matches/matchNavigation";
import ResultClaimControl from "../Matches/ResultClaimControl";
import { isCompletedDetail } from "../Matches/completedResults";

function formatScope(scope) {
  return scope === "stage" ? "Stage" : "Match";
}

function formatNumber(value) {
  return Number.isFinite(value) ? value : 0;
}

function formatTime(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num.toFixed(2) : "0.00";
}

function LeaderboardScoreDetail() {
  const { scope, id, sk } = useParams();
  const location = useLocation();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeView, setActiveView] = useState("breakdown");
  const [matchStages, setMatchStages] = useState([]);
  const [stageNamesError, setStageNamesError] = useState("");
  const [stageNamesRetry, setStageNamesRetry] = useState(0);
  const [claimMatch, setClaimMatch] = useState(null);
  const [claimMatchError, setClaimMatchError] = useState(false);
  const [claimMatchRetry, setClaimMatchRetry] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    setPayload(null);
    setActiveView("breakdown");

    async function load() {
      try {
        const value =
          scope === "stage"
            ? await getStageScoreDetail(id, sk)
            : await getMatchScoreDetail(id, sk);

        if (!active) {
          return;
        }

        setPayload(value);
      } catch (err) {
        if (!active) {
          return;
        }

        setError(err.message || "Failed to load score detail");
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    if (scope !== "match" && scope !== "stage") {
      setError("Invalid score scope. Use 'match' or 'stage'.");
      setLoading(false);
      return;
    }

    load();

    return () => {
      active = false;
    };
  }, [scope, id, sk]);

  const leaderboard = useMemo(() => payload?.leaderboard || {}, [payload]);
  const competitorName = leaderboard?.competitorName || "Competitor";
  const scoreDetail = useMemo(() => payload?.scoreDetail || {}, [payload]);
  const auditEvents = useMemo(() => {
    const events = Array.isArray(scoreDetail?.auditEvents) ? scoreDetail.auditEvents : [];
    return events.filter(event => event && typeof event === "object");
  }, [scoreDetail]);
  const hasAuditEvents = auditEvents.length > 0;
  const matchId = scoreDetail.matchId || leaderboard.matchId || (scope === "match" ? id : null);
  const participantId = leaderboard.participantId || scoreDetail.participantId;
  useEffect(() => {
    let active = true;
    setClaimMatch(null);
    setClaimMatchError(false);
    if (!matchId || !participantId) return undefined;
    getMatch(matchId).then(value => { if (active) setClaimMatch(value); })
      .catch(error => { if (active) setClaimMatchError(error.status === 404 ? "unavailable" : "error"); });
    return () => { active = false; };
  }, [matchId, participantId, claimMatchRetry]);
  useEffect(() => {
    let active = true;
    setMatchStages([]);
    setStageNamesError("");
    if (scope !== "match" || !matchId || !Array.isArray(scoreDetail.stageScores)) return;
    async function loadStageNames() {
      try {
        const setup = await getMatchSetupDetail(matchId);
        const stages = setup?.setupDetail?.stages;
        if (!Array.isArray(stages) || stages.length === 0) throw new Error("Stage names unavailable");
        if (active) setMatchStages(stages);
      } catch {
        if (active) setStageNamesError("Stage names could not load. Scores remain available with stage identifiers.");
      }
    }
    loadStageNames();
    return () => { active = false; };
  }, [scope, matchId, scoreDetail, stageNamesRetry]);
  const affiliationsText = Array.isArray(leaderboard?.affiliations) && leaderboard.affiliations.length > 0
    ? leaderboard.affiliations.join(", ")
    : "-";

  const stageTimingSummary = useMemo(() => {
    if (scope !== "stage") {
      return null;
    }

    const hasRawTime = Object.prototype.hasOwnProperty.call(scoreDetail, "recordedStageTimeSeconds");
    return {
      mode: hasRawTime ? "Raw Time (RO Entered)" : "Limited (Base 0s)",
      rawTime: hasRawTime ? formatTime(scoreDetail?.recordedStageTimeSeconds) : "-",
      additionalPenalty: formatNumber(scoreDetail?.additionalTimePenaltySeconds),
      lateShots: formatNumber(scoreDetail?.lateShotCount)
    };
  }, [scope, scoreDetail]);

  const quickStats = useMemo(() => {
    const primaryScore = scope === "stage" ? formatTime(leaderboard?.stageScore) : formatTime(leaderboard?.totalScore);
    return [
      { label: `${formatScope(scope)} Score`, value: `${primaryScore} s` },
      { label: "A-Zone Hits", value: formatNumber(leaderboard?.xCount) },
      { label: "Tie Breaker", value: `${formatTime(leaderboard?.tieBreaker)} s` },
      { label: "Division", value: leaderboard?.divisionId || "-" }
    ];
  }, [scope, leaderboard]);

  return (
    <section className="leaderboard-page portal-surface score-detail-page">
      <Helmet>
        <title>Competitor Score Detail | Range Pocket Competition</title>
      </Helmet>

      <div className="leaderboard-header">
        <Link to={resultsReturn(location, matchId)} state={location.state} className="back-link">
          {matchId ? "Back to match" : "Back to matches"}
        </Link>
        <h1>{formatScope(scope)} Competitor Score</h1>
        <p>{competitorName}</p>
      </div>

      {loading && (
        <div className="leaderboard-card list-empty-panel status-loading-panel">
          <div className="owner-registration-section-label">Loading</div>
          <strong>Score Detail</strong>
        </div>
      )}
      {!loading && error && (
        <section className="leaderboard-card app-gate-panel score-detail-error-panel">
          <div className="app-gate-eyebrow">Score detail</div>
          <h2>Score detail could not load</h2>
          <p>{error}</p>
          <div className="app-gate-actions">
            <button type="button" className="match-search-button" onClick={() => window.location.reload()}>
              Try Again
            </button>
            <Link to="/matches" className="registration-auth-secondary leaderboard-inline-link">
              Matches
            </Link>
            <Link to="/tournaments" className="registration-auth-secondary leaderboard-inline-link">
              Tournaments
            </Link>
          </div>
        </section>
      )}

      {!loading && !error && payload && (
        <>
          {claimMatch && <ResultClaimControl family="arc" matchId={matchId} participantId={participantId} competitorName={competitorName} eligible={isCompletedDetail(claimMatch)} />}
          {!participantId && <ResultClaimControl family="arc" matchId={matchId} competitorName={competitorName} />}
          {claimMatchError && <div className="result-claim" role="status"><p>{claimMatchError === "unavailable" ? "Match no longer available. This score cannot be claimed." : "Match availability could not load. Scores are still available."}</p>{claimMatchError !== "unavailable" && <button type="button" className="result-claim-secondary" onClick={() => setClaimMatchRetry(value => value + 1)}>Retry match availability</button>}</div>}
          {activeView === "audit" ? (
            <div className="score-detail-audit-context">
              <strong>{formatTime(scope === "stage" ? leaderboard?.stageScore : leaderboard?.totalScore)} s</strong>
              <span>{auditEvents[0]?.entryLabel || "Entry"} · {auditEvents[0]?.stageName || "Stage score"}</span>
            </div>
          ) : <div className="match-stat-grid">
            {quickStats.map((item) => (
              <div key={item.label} className="match-stat-card">
                <div className="match-stat-label">{item.label}</div>
                <div className="match-stat-value">{item.value}</div>
              </div>
            ))}
          </div>}

          <div className={`mode-switch-wrap ${activeView === "audit" ? "score-detail-audit-tabstrip" : "leaderboard-card"}`}>
            <div className="mode-switch" role="tablist" aria-label="Score detail sections">
              <button
                type="button"
                role="tab"
                aria-selected={activeView === "breakdown"}
                className={`mode-switch-btn ${activeView === "breakdown" ? "active" : ""}`}
                onClick={() => setActiveView("breakdown")}
              >
                Breakdown
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeView === "summary"}
                className={`mode-switch-btn ${activeView === "summary" ? "active" : ""}`}
                onClick={() => setActiveView("summary")}
              >
                Summary
              </button>
              {hasAuditEvents && (
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeView === "audit"}
                  className={`mode-switch-btn ${activeView === "audit" ? "active" : ""}`}
                  onClick={() => setActiveView("audit")}
                >
                  Owner Audit
                </button>
              )}
            </div>
          </div>

          {activeView === "summary" && (
            <div className="leaderboard-columns">
              <section className="leaderboard-card">
                <h2>Competitor</h2>
                <div className="json-kv-grid">
                  <div className="json-kv-item">
                    <div className="json-kv-label">Name</div>
                    <div className="json-kv-value">{competitorName}</div>
                  </div>
                  <div className="json-kv-item">
                    <div className="json-kv-label">Scope</div>
                    <div className="json-kv-value">{leaderboard?.scope || scope}</div>
                  </div>
                </div>
              </section>

              <section className="leaderboard-card">
                <h2>Context</h2>
                <div className="json-kv-grid">
                  {leaderboard.teamName && <div className="json-kv-item"><div className="json-kv-label">Team</div><div className="json-kv-value">{leaderboard.teamName}</div></div>}
                  {affiliationsText && affiliationsText !== "-" && <div className="json-kv-item"><div className="json-kv-label">Affiliations</div><div className="json-kv-value">{affiliationsText}</div></div>}
                  {leaderboard.isNraAmbassador && <div className="json-kv-item"><div className="json-kv-label">NRA Ambassador</div><div className="json-kv-value">Yes</div></div>}
                  <div className="json-kv-item">
                    <div className="json-kv-label">Updated</div>
                    <div className="json-kv-value">{leaderboard?.updatedAt ? new Date(leaderboard.updatedAt).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" }) : "Update time unavailable"}</div>
                  </div>
                  {stageTimingSummary && (
                    <>
                      <div className="json-kv-item">
                        <div className="json-kv-label">Timing Mode</div>
                        <div className="json-kv-value">{stageTimingSummary.mode}</div>
                      </div>
                      <div className="json-kv-item">
                        <div className="json-kv-label">Raw Stage Time</div>
                        <div className="json-kv-value">{stageTimingSummary.rawTime}{stageTimingSummary.rawTime !== "-" ? " s" : ""}</div>
                      </div>
                      <div className="json-kv-item">
                        <div className="json-kv-label">Additional Penalty</div>
                        <div className="json-kv-value">{stageTimingSummary.additionalPenalty}s</div>
                      </div>
                      <div className="json-kv-item">
                        <div className="json-kv-label">Late Shots</div>
                        <div className="json-kv-value">{stageTimingSummary.lateShots}</div>
                      </div>
                    </>
                  )}
                </div>
                {stageTimingSummary && (
                  <p className="leaderboard-meta">
                    RO-entered stages include a raw stage time; limited stages use base 0s and only penalties add time.
                  </p>
                )}
              </section>
            </div>
          )}

          {activeView === "breakdown" && (
            <section className="leaderboard-card">
              <h2>Score Breakdown</h2>
              {stageNamesError && (
                <div role="status" className="owner-banner-message">
                  <p>{stageNamesError}</p>
                  <button type="button" className="match-search-button" onClick={() => setStageNamesRetry(value => value + 1)}>
                    Retry Stage Names
                  </button>
                </div>
              )}
              <ScoreDetailRenderer scoreDetail={payload?.scoreDetail} variant="arc" matchStages={matchStages} showSummary={false} showHeading={false} />
            </section>
          )}

          {activeView === "audit" && hasAuditEvents && (
            <ScoreActionLog key={`${scope}:${id}:${sk}`} events={auditEvents} competitorName={competitorName} />
          )}
        </>
      )}
    </section>
  );
}

export const LeaderboardScoreDetail_Page = {
  path: "/matches/results/:scope/:id/:sk",
  element: <LeaderboardScoreDetail />
};
